<template>
  <main class="container mx-auto mt-16 py-5 md:pt-10 md:pb-15 md:w-full">
    <section class="">
      <h1
        class="text-[22px] leading-normal md:text-[40px] md:leading-[50px] font-medium text-bw-900"
      >
        Search Results
      </h1>

      <form
        class="site-search w-full flex items-center border-b border-gray-600 mt-8"
        @submit.prevent="onSiteSearch"
      >
        <button aria-label="search-button" type="submit" class="mr-4">
          <NuxtIcon name="search" filled class="search-icon block" />
        </button>
        <input
          id="siteSearch"
          v-model="search"
          type="text"
          name="site-search"
          class="w-full focus-visible:outline-none placeholder:text-bw-300"
          placeholder="Type to Search Case Studies, Sustainability, and Ideas"
        />
      </form>
    </section>

    <section class="results mt-10">
      <UtilsSkeletonLoading v-if="isLoading" class="w-32 h-8" />
      <p v-else class="text-bw-800 text-base">
        Found <b>{{ searchMeta.pagination.total }}</b> results
      </p>

      <div
        v-if="searchMeta.pagination.total === 0"
        class="w-full mt-8 text-center"
      >
        <template v-if="isLoading">
          <div
            v-for="i in 3"
            :key="i"
            class="flex flex-col md:flex-row gap-x-8 result-card mb-12"
          >
            <div class="w-full md:w-1/3 lg:w-[14%]">
              <UtilsSkeletonLoading class="w-full h-48 md:h-32" />
            </div>
            <div class="flex flex-col justify-center w-full gap-2 mt-2">
              <UtilsSkeletonLoading class="w-1/4 md:w-20 h-6" />
              <UtilsSkeletonLoading class="w-10/12 md:w-1/2 h-8" />
              <UtilsSkeletonLoading class="w-full md:w-10/12 h-14 md:h-7" />
            </div>
          </div>
        </template>
        <div v-else class="mb-28">
          <h2
            class="text-[22px] leading-normal md:text-[40px] md:leading-[50px] font-medium text-bw-900 mb-5 mx-auto"
          >
            We Looked Everywhere
          </h2>
          <p
            class="text-sm sm:text-base text-bw-800 w-[90%] sm:w-3/4 md:w-2/5 mx-auto"
          >
            Sorry, the page you are looking for cannot be found. Please try to
            enter another keyword.
          </p>
        </div>
      </div>

      <div v-else class="flex flex-col mt-4 gap-8 md:gap-12 result-list">
        <template v-if="isLoading || paginationLoading">
          <div
            v-for="i in 3"
            :key="i"
            class="flex flex-col md:flex-row gap-x-8 result-card"
          >
            <div class="w-full md:w-1/3 lg:w-[14%]">
              <UtilsSkeletonLoading class="w-full h-48 md:h-32" />
            </div>
            <div class="flex flex-col justify-center w-full gap-2 mt-2">
              <UtilsSkeletonLoading class="w-1/4 md:w-20 h-6" />
              <UtilsSkeletonLoading class="w-10/12 md:w-1/2 h-8" />
              <UtilsSkeletonLoading class="w-full md:w-10/12 h-14 md:h-7" />
            </div>
          </div>
        </template>
        <template v-else>
          <NuxtLink
            v-for="item in searchResults"
            :key="item.id"
            :to="`${itemUrl(item.attributes?.layout)}/${item.attributes?.slug}`"
            :title="item.attributes?.card_title"
            class="flex flex-col sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-8 result-card"
          >
            <div class="relative col-span-1 flex items-center">
              <NuxtImg
                format="webp"
                srcset=""
                class="w-full rounded-md"
                :src="
                  item.attributes?.image?.data?.attributes?.formats?.large
                    ?.url ||
                  item.attributes?.image?.data?.attributes?.formats?.medium
                    ?.url ||
                  item.attributes?.image?.data?.attributes?.url ||
                  '/assets/img/404.png'
                "
                :alt="item.attributes?.card_title"
                :title="item.attributes?.card_title"
                loading="lazy"
              />
              <NuxtImg
                v-if="withLogo"
                format="webp"
                srcset=""
                class="absolute top-3 left-2 filter-white z-[2] w-10 sm:w-6"
                src="/assets/img/idx.png"
                alt="Case logo"
                title="Case logo"
                loading="lazy"
              />
              <div
                v-if="withLogo"
                class="card-overlay absolute w-full h-full top-0 rounded-md"
              ></div>
            </div>

            <div
              class="flex flex-col sm:col-span-2 md:col-span-3 lg:col-span-5 justify-center"
            >
              <p class="text-xs md:text-sm my-2 sm:mb-2 text-bw-600">
                {{ useFormattedDate(item.attributes?.publish_at) }}
              </p>
              <h2
                class="text-base md:text-lg text-bw-900 font-medium mb-1 result-card--title"
              >
                {{ item.attributes?.card_title }}
              </h2>
              <p
                v-if="item.attributes?.layout_component?.[0]?.description"
                class="text-sm md:text-base text-bw-800"
              >
                {{ item.attributes?.layout_component?.[0]?.description }}
              </p>
            </div>
          </NuxtLink>
        </template>
      </div>

      <Pagination
        v-if="searchMeta.pagination.pageCount !== 0"
        class="mt-3 md:mt-10"
        :total-pages="searchMeta.pagination.pageCount"
        :total-items="searchMeta.pagination.total"
        :current-page="searchMeta.pagination.page"
        :max-visible-buttons="6"
        @pagechanged="changePage"
      />
    </section>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

defineProps({
  withLogo: {
    type: Boolean,
    default: false
  }
})

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const route = useRoute()
const router = useRouter()

const search = ref(route.query.keyword || '')
const currentPage = ref(route.query.page || 1)
// const pageSize = ref(5)

const isLoading = ref(true)
const paginationLoading = ref(false)
const searchResults = ref([])
const searchMeta = ref([])
const SEOMeta = ref({})

const [responseSearch, seo] = await Promise.all([
  useSuitmediaApiData(`/api/contents`, {
    query: {
      'pagination[page]': 1,
      'pagination[pageSize]': 5,
      populate: '*',
      'filters[title][$contains]': search.value,
      'sort[0]': 'publish_at:desc'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  })
])

isLoading.value = responseSearch.pending.value

if (responseSearch.error.value) {
  throw createError({
    statusCode: 404,
    message: 'Contents does not exist'
  })
}

searchResults.value = responseSearch.data.value.data
searchMeta.value = responseSearch.data.value.meta
SEOMeta.value = seo.data.value.data[0]

const getSearchResults = async () => {
  const searchVal = route.query.keyword || ''
  search.value = searchVal

  // const pageVal = route.query.page

  const query = {
    'pagination[page]': route.query.page || currentPage,
    'pagination[pageSize]': 5,
    populate: '*',
    'filters[title][$contains]': searchVal,
    'sort[0]': 'publish_at:desc'
  }

  if (!search.value) {
    delete query['filters[title][$contains]']
  }

  const { data, error, pending } = await useSuitmediaApiData(`/api/contents`, {
    query
  })

  isLoading.value = pending.value
  paginationLoading.value = pending.value

  searchResults.value = data.value.data
  searchMeta.value = data.value.meta

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: 'Contents does not exist'
    })
  }
}

const onSiteSearch = () => {
  isLoading.value = true
  currentPage.value = 1

  router.push({
    query: {
      keyword: search.value,
      page: currentPage.value
    }
  })

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const itemUrl = layout => {
  if (layout === 'study-case') {
    return '/work'
  } else if (layout === 'video') {
    return '/videos'
  } else if (layout === 'podcast') {
    return ''
  } else if (layout === 'sustainability') {
    return '/sustainability'
  } else if (layout === 'article') {
    return '/ideas'
  }
}

const changePage = page => {
  paginationLoading.value = true
  const currentQuery = route.query
  currentPage.value = page
  delete currentQuery.page

  router.push({
    query: {
      ...currentQuery,
      page
    }
  })

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

watch(
  () => route.query,
  () => {
    getSearchResults()
  }
)
</script>

<style lang="scss" scoped>
.site-search {
  padding: 12px 16px;
  background: linear-gradient(#a5a5a5, #a5a5a5),
    linear-gradient(#a5a5a5, #a5a5a5), linear-gradient(#a5a5a5, #a5a5a5);
  background-size:
    1px 20%,
    100% 1px,
    1px 20%;
  background-position:
    bottom left,
    bottom center,
    bottom right;
  background-repeat: no-repeat;

  border: none;
}

.result-card {
  &:hover {
    .result-card--title {
      color: #ff6700;
    }
  }
}

:deep(.search-icon) {
  color: #ff6700 !important;
  &:hover svg path {
    fill: #ff6700 !important;
  }
  svg path {
    fill: #a5a5a5 !important;
  }
}
.filter-white {
  filter: brightness(0) invert(1);
}

.card-overlay {
  background: linear-gradient(
    153deg,
    rgba(0, 0, 0, 1) 50%,
    rgba(0, 0, 0, 1) 100%
  );
  opacity: 0.1;
}
</style>
